/**
 * Which phase(s) of a casing job a FIELD hand is shown, and each one's date.
 *
 * TNT J-4404: a wash-only hand saw the job dated Nov 9 (the run) under Start
 * Wash, while the wash was on Nov 4. The date printed was always
 * `scheduled_date`, whichever phase the button named.
 *
 * THE RULE (operator, 2026-11-03):
 *   - wash-only hand → only the wash, with the wash date
 *   - run-only hand  → only the run, with the run date
 *   - 'both' or NULL → both phases, each with its own date
 *
 * Both columns are job WALL-CLOCK values (see "THE JOB WALL-CLOCK COLUMNS" in
 * calendar-date.ts): the office's typed date and time stamped UTC verbatim.
 * They are read as written with `formatCalendarDate` / `formatScheduledTime`
 * and NEVER converted through the tenant zone.
 *
 * NO FALLBACK. A hand whose crew phase names a phase this job does not have
 * (a wash hand on a job with no wash date) gets NO phase here, not the job's
 * other phase. Falling back put one phase's button under the other phase's
 * date. The screen says so instead — see `phaseMismatch`.
 */

export type FieldPhase = 'wash' | 'run';
export type MyPhase = 'wash' | 'run' | 'both' | null;

export const PHASE_WORD: Record<FieldPhase, string> = { wash: 'Wash', run: 'Run' };

/** Whatever the wire sent, onto the vocabulary. Anything else is NULL (every phase). */
export function normalizeMyPhase(raw: unknown): MyPhase {
  return raw === 'wash' || raw === 'run' || raw === 'both' ? raw : null;
}

const pick = (job: any, camel: string, snake: string): string | null => {
  const v = job?.[camel] ?? job?.[snake];
  return typeof v === 'string' && v.trim() !== '' ? v : null;
};

/** The phase's raw wall-clock value: the wash date, or `scheduled_date` for the run. */
export function phaseDateValue(job: any, phase: FieldPhase): string | null {
  return phase === 'wash' ? pick(job, 'dateTimeWash', 'date_time_wash') : pick(job, 'scheduledDate', 'scheduled_date');
}

/** True when the office has dated a wash. A same-day wash counts. */
export function hasWashDate(job: any): boolean {
  return phaseDateValue(job, 'wash') != null;
}

/** The job's phases: the run always, the wash only when it is dated. */
export function jobPhases(job: any): FieldPhase[] {
  return hasWashDate(job) ? ['wash', 'run'] : ['run'];
}

/**
 * The phases this hand works on this job. 'both'/NULL = every phase the job
 * has. A single phase the job lacks yields `[]` — never the other phase.
 */
export function handPhases(job: any, myPhase: unknown): FieldPhase[] {
  const mine = normalizeMyPhase(myPhase);
  const phases = jobPhases(job);
  if (mine === 'wash' || mine === 'run') return phases.filter((p) => p === mine);
  return phases;
}

/** A single-phase hand on a job without that phase — the case that used to fall back. */
export function phaseMismatch(job: any, myPhase: unknown): FieldPhase | null {
  const mine = normalizeMyPhase(myPhase);
  if ((mine === 'wash' || mine === 'run') && handPhases(job, mine).length === 0) return mine;
  return null;
}

/**
 * The phases a screen shows (dates and Start buttons): the route's day phase
 * when the hand works it, otherwise every phase the hand works.
 */
export function shownPhases(job: any, myPhase: unknown, routePhase?: unknown): FieldPhase[] {
  const mine = handPhases(job, myPhase);
  if ((routePhase === 'wash' || routePhase === 'run') && mine.includes(routePhase)) return [routePhase];
  return mine;
}

/**
 * The day the hand next works this job, as a raw wall-clock value — for
 * sorting. One phase: that phase's date. Both: the earlier dated one, which is
 * when they first have to be there. Compared as strings on the wall-clock
 * prefix, so no zone is involved.
 */
export function handSortDate(job: any, myPhase: unknown): string | null {
  const dates = handPhases(job, myPhase)
    .map((p) => phaseDateValue(job, p))
    .filter((d): d is string => d != null)
    .sort();
  return dates[0] ?? null;
}

/**
 * The phase a field-home row's chip names. The API narrows a same-day row's
 * `day_phase` to a single-phase hand's own phase; this holds the same rule on
 * a payload from before that (or an offline copy): a wash-only hand's
 * same-day row reads Thread Wash, never "Wash + Run". It never widens — a
 * 'wash' or 'run' row is that row's day and stays so.
 *
 * TEMPORARY. It is the API's answer (crewDayPhaseSql, api#1346) restated
 * client-side for the deploy gap and for offline copies cached before it.
 * REMOVE the narrowing line below once api#1346 has been live long enough
 * that no cached dashboard predates it, and read `day_phase` as sent —
 * otherwise it is a permanent second answer to one question.
 */
export function dashboardRowPhase(row: { day_phase?: unknown; my_phase?: unknown }): 'wash' | 'run' | 'both' | null {
  const day = row.day_phase === 'wash' || row.day_phase === 'run' || row.day_phase === 'both' ? row.day_phase : null;
  const mine = normalizeMyPhase(row.my_phase);
  if (day === 'both' && (mine === 'wash' || mine === 'run')) return mine;
  return day;
}
